import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { KpiMode } from '../../kpi-engine';

import KpiGridTable from '../../Components/KpiGridTable';
import ProjectMap from '../../Components/ProjectMap/ProjectMap';
import type { ProjectMapPoint } from '../../Components/ProjectMap/types';
import ResizableSplitPane from '../../Components/ResizableSplitPane/ResizableSplitPane';
import ProjectDetailPanel from '../ProjectDetail/ProjectDetailPanel';
import GroupProgressPage from '../../Components/GroupProgressPage/GroupProgressPage';
import PortfolioSummaryPanel from '../../Components/PortfolioSummaryPanel/PortfolioSummaryPanel';

import ProjectCard from '../../Components/ProjectCard/ProjectCard';
import type { CommunityPathSegment } from '../../Components/ProjectCard/ProjectCard';
import CreateProjectCard from '../../Components/ProjectStepper/CreateProjectCard';
import cardStyles from '../../Components/ProjectCard/ProjectCard.module.css';
import { resolveCommunityAncestors } from '../../Data/ProjectConfig/projectConfigOverrides';
import { communityConfigStore } from '../../Data/store';
import { useFocus } from '../../state/FocusContext';
import { useProjectSelection } from '../../state/ProjectSelectionContext';
import { indicatorBucket, type IndicatorBucket } from '../../utils/indicatorBuckets';
import { tabIndexToField } from '../../state/focusRouting';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

type Props = {
  data: any[];
  dataVersion?: number;
  kpiMode: KpiMode;
  onKpiModeChange: (m: KpiMode) => void;
  debugMode: boolean;
  onDebugModeChange: (v: boolean) => void;
  currentUserGuid: string;
  onUserChange: (userGuid: string) => void;
  authorizedCollectionGuids?: any;
  apiCollections?: Record<string, any>[];
  apiCollectionProjects?: Record<string, any>[];
  apiSchemaFields?: Record<string, any>[];
  apiMetadataValues?: Record<string, any>[];
  apiCustomEntities?: Record<string, any>[];
  onProjectConfigChanged: () => void;
  searchText: string;
  onSearchTextChange: (next: string) => void;
  /** Project ACL entries (seed-shape: { project_guid, user_guid, role, is_active }). */
  apiProjectAcl?: Record<string, any>[];
  /** Community entities for country-flag resolution on project cards. */
  apiCommunityEntities?: Record<string, any>[];
  /** Parent-child relationships for walking entity hierarchy to find country flags. */
  apiCommunityRelationships?: Record<string, any>[];
};

/* ------------------------------------------------------------------ */
/*  Helpers (copied from original MyProjectsContent)                   */
/* ------------------------------------------------------------------ */

function collectPrimitiveStrings(input: any, out: string[], depth: number, seen: Set<any>) {
  if (input == null) return;
  if (depth <= 0) return;
  const t = typeof input;
  if (t === 'string') { const s = input.trim(); if (s) out.push(s); return; }
  if (t === 'number' || t === 'boolean') { out.push(String(input)); return; }
  if (input instanceof Date && Number.isFinite(input.getTime())) { out.push(input.toISOString()); return; }
  if (Array.isArray(input)) { for (const v of input) collectPrimitiveStrings(v, out, depth - 1, seen); return; }
  if (t === 'object') {
    if (seen.has(input)) return;
    seen.add(input);
    for (const k of Object.keys(input)) {
      const lk = String(k || '').toLowerCase();
      if (lk.includes('geometry') || lk.includes('geojson') || lk.includes('wkt')) continue;
      collectPrimitiveStrings(input[k], out, depth - 1, seen);
    }
  }
}

function buildSearchBlob(row: any): string {
  try {
    const parts: string[] = [];
    const seen = new Set<any>();
    collectPrimitiveStrings(row, parts, 4, seen);
    if (row?.project) collectPrimitiveStrings(row.project, parts, 4, seen);
    return parts.join(' \n ').toLowerCase();
  } catch { return ''; }
}

/* ------------------------------------------------------------------ */
/*  Responsive breakpoint                                              */
/* ------------------------------------------------------------------ */

const SPLIT_MIN_WIDTH = 900; // below this, detail takes over full screen

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function MyProjectsPage(props: Props) {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();
  const location = useLocation();
  const restoreWorkSubTab = (location.state as any)?.workSubTab as string | undefined;
  const {
    data,
    dataVersion,
    kpiMode,
    onKpiModeChange,
    debugMode,
    onDebugModeChange,
    currentUserGuid,
    onUserChange,
    authorizedCollectionGuids,
    apiCollections,
    apiCollectionProjects,
    apiSchemaFields,
    apiMetadataValues,
    apiCustomEntities,
    onProjectConfigChanged,
    searchText,
    onSearchTextChange,
    apiProjectAcl,
    apiCommunityEntities,
    apiCommunityRelationships,
  } = props;

  const gridKeyBase = String(dataVersion ?? 0);

  const { selectedProjectGuid, pulseProjectGuid, selectProject } = useFocus();
  const { mapColorMode, legendBucketFilter, setLegendBucketFilter } = useProjectSelection();

  // DEV-945: initial view hardcoded to 'split'. Was reading from a per-user
  // localStorage preference (getDefaultProjectView), but the App Settings
  // dropdown that wrote to it was retired with this ticket. Users still
  // switch live via the in-page view buttons.
  const [myProjectsView, setMyProjectsView] = useState<'table' | 'map' | 'split' | 'cards'>('split');

  /* ---- Selected project data row (for detail panel) ---- */
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);
  /** KPI tab index from the grid click (0=Summary..4=Data), or -1 for "no KPI request". */
  const [activeKpiTabIndex, setActiveKpiTabIndex] = useState<number>(-1);
  /** Monotonic counter: bumped on every grid selection so the detail panel
   *  can detect repeated clicks on the same project / tab combination. */
  const [tabRequestId, setTabRequestId] = useState(0);

  /* ---- Group selection (for GroupProgressPage) ---- */
  const [selectedGroupPayload, setSelectedGroupPayload] = useState<any | null>(null);
  /** Snapshot of previous project selection for back-button restore. */
  const previousProjectRef = useRef<{ guid: string; tabIndex: number } | null>(null);

  const selectedProjectRow = useMemo(() => {
    if (!selectedGuid) return null;
    return (data || []).find((d: any) => String(d?.project_guid ?? '') === selectedGuid) ?? null;
  }, [data, selectedGuid]);

  /* ---- Responsive detection ---- */
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const useFullScreenDetail = containerWidth < SPLIT_MIN_WIDTH;

  /* Split view resizing (map/grid vertical split) */
  const [splitTopPct, setSplitTopPct] = useState<number>(55);
  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const splitDragRef = useRef<{ dragging: boolean; startY: number; startPct: number } | null>(null);

  /* ---- Map points ---- */
  const mapPoints = useMemo<ProjectMapPoint[]>(() => {
    return (data || [])
      .map((d: any) => {
        const p = d?.project || {};
        const rawLat = p?.location_lat;
        const rawLng = p?.location_lon;
        const lat = rawLat === '' || rawLat == null ? NaN : Number(rawLat);
        const lng = rawLng === '' || rawLng == null ? NaN : Number(rawLng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        if (lat === 0 && lng === 0) return null;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
        return {
          project_guid: String(d?.project_guid ?? ''),
          name: String(p?.project_name ?? 'Untitled'),
          lat, lng,
          summary_indicator_color: d?.summary_indicator_color ?? null,
          schedule_indicator_color: d?.schedule_indicator_color ?? null,
          mission_indicator_color: d?.mission_indicator_color ?? null,
          finance_indicator_color: d?.finance_indicator_color ?? null,
          data_reliability_color: d?.data_reliability_color ?? null,
          // DEV-851: Project the row's _isCompletedProject flag so the map
          // can render the blue Complete state for closed projects when
          // viewing the Data Reliability mode.
          _isCompletedProject: !!(d as any)?._isCompletedProject,
        } as ProjectMapPoint;
      })
      .filter(Boolean) as ProjectMapPoint[];
  }, [data]);

  /* ---- Search / Filter ---- */
  /** Split on whitespace into individual lowercase terms for AND matching. */
  const searchTerms = useMemo(() => {
    const raw = String(searchText || '').trim().toLowerCase();
    if (!raw) return [] as string[];
    return raw.split(/\s+/).filter(Boolean);
  }, [searchText]);

  const projectSearchIndex = useMemo(() => {
    const idx = new Map<string, string>();
    for (const row of data || []) {
      const guid = String(row?.project_guid ?? '');
      if (!guid) continue;
      idx.set(guid, buildSearchBlob(row));
    }
    return idx;
  }, [data]);

  const matchedProjectGuidSet = useMemo(() => {
    if (searchTerms.length === 0) return null;
    const set = new Set<string>();
    for (const row of data || []) {
      const guid = String(row?.project_guid ?? '');
      if (!guid) continue;
      const blob = projectSearchIndex.get(guid) || '';
      // AND logic: every term must appear somewhere in the blob
      if (searchTerms.every((term) => blob.includes(term))) set.add(guid);
    }
    return set;
  }, [data, projectSearchIndex, searchTerms]);

  const bucketForRow = useCallback(
    (row: any, mode: string): IndicatorBucket => {
      const p = row?.project || row || {};
      // DEV-851: blue Complete-state override on the Data Reliability mode.
      // _isCompletedProject lives on the row itself (set by App.tsx), not
      // on the nested project object.
      const isCompleted = !!(row as any)?._isCompletedProject;
      switch (mode) {
        case 'schedule': return indicatorBucket(p.schedule_indicator_color);
        case 'finance': return indicatorBucket(p.finance_indicator_color);
        case 'mission': return indicatorBucket(p.mission_indicator_color);
        case 'data':
          if (isCompleted) return 'completed';
          return indicatorBucket(p.data_reliability_color);
        default: return indicatorBucket(p.summary_indicator_color);
      }
    },
    []
  );

  const filteredMapPoints = useMemo(() => {
    if (!matchedProjectGuidSet) return mapPoints;
    return mapPoints.filter((p: any) => matchedProjectGuidSet.has(String(p?.project_guid ?? '')));
  }, [mapPoints, matchedProjectGuidSet]);

  const filteredTableData = useMemo(() => {
    let rows = data || [];
    if (matchedProjectGuidSet) {
      rows = rows.filter((r: any) => matchedProjectGuidSet.has(String(r?.project_guid ?? '')));
    }
    if (legendBucketFilter) {
      rows = rows.filter((r: any) => bucketForRow(r, mapColorMode) === legendBucketFilter);
    }
    return rows;
  }, [bucketForRow, data, legendBucketFilter, mapColorMode, matchedProjectGuidSet]);

  /* ---- Selection → open detail panel (NOT navigate away) ---- */
  const handleSelectProject = useCallback(
    (item: any, tabIndex: number = 0) => {
      // ── Group row clicked → show GroupProgressPage ──
      if (item?.__type === "group") {
        // Snapshot current project selection for back-button restore
        if (selectedGuid) {
          previousProjectRef.current = { guid: selectedGuid, tabIndex: activeKpiTabIndex };
        } else {
          previousProjectRef.current = null;
        }
        setSelectedGroupPayload(item);
        setSelectedGuid(null);
        setActiveKpiTabIndex(tabIndex);
        return;
      }

      // ── Project row clicked ──
      const guid = String(item?.project_guid ?? '');
      if (!guid) return;
      selectProject(guid, { pulse: true });
      setSelectedGuid(guid);
      setSelectedGroupPayload(null); // clear any group view
      setActiveKpiTabIndex(tabIndex);
      setTabRequestId((n) => n + 1);
    },
    [selectProject, selectedGuid, activeKpiTabIndex]
  );

  /* ---- GroupProgressPage: back button handler ---- */
  const handleGroupBack = useCallback(() => {
    const prev = previousProjectRef.current;
    setSelectedGroupPayload(null);
    if (prev) {
      selectProject(prev.guid, { pulse: true });
      setSelectedGuid(prev.guid);
      setActiveKpiTabIndex(prev.tabIndex);
    } else {
      setSelectedGuid(null);
      setActiveKpiTabIndex(-1);
    }
    previousProjectRef.current = null;
  }, [selectProject]);

  /* ---- GroupProgressPage: project clicked inside group view ---- */
  const handleGroupSelectProject = useCallback(
    (item: any, tabIndex: number) => {
      const guid = String(item?.project_guid ?? item?.project?.project_guid ?? '');
      if (!guid) return;
      selectProject(guid, { pulse: true });
      setSelectedGuid(guid);
      setSelectedGroupPayload(null);
      setActiveKpiTabIndex(tabIndex);
      setTabRequestId((n) => n + 1);
    },
    [selectProject],
  );

  const handleMapClick = useCallback(
    (guid: string, _mode: string) => {
      selectProject(guid, { pulse: true });
      setSelectedGuid(guid);
      setSelectedGroupPayload(null);
      /* Map marker click → open Core tab with hero visible */
      setActiveKpiTabIndex(-1);
      setTabRequestId((n) => n + 1);

      /* If the clicked project falls outside the current legend filter,
         clear the filter so the grid row becomes visible. */
      if (legendBucketFilter) {
        const row = (data || []).find((d: any) => String(d?.project_guid ?? '') === guid);
        if (row && bucketForRow(row, mapColorMode) !== legendBucketFilter) {
          setLegendBucketFilter(null);
        }
      }
    },
    [selectProject, legendBucketFilter, data, bucketForRow, mapColorMode, setLegendBucketFilter]
  );

  const handleCloseDetail = useCallback(() => {
    setSelectedGuid(null);
    setActiveKpiTabIndex(-1);
  }, []);

  /* Bidirectional: detail panel KPI tab change → update grid highlight */
  const handleDetailKpiTabChange = useCallback((idx: number) => {
    setActiveKpiTabIndex(idx);
  }, []);

  /* ---- Split drag (map/grid vertical) ---- */
  const beginDrag = (e: React.MouseEvent) => {
    const container = splitContainerRef.current;
    if (!container) return;
    splitDragRef.current = { dragging: true, startY: e.clientY, startPct: splitTopPct };
    e.preventDefault();
  };

  const onMouseMove = useCallback((e: MouseEvent) => {
    const d = splitDragRef.current;
    const container = splitContainerRef.current;
    if (!d?.dragging || !container) return;
    const rect = container.getBoundingClientRect();
    const deltaY = e.clientY - d.startY;
    const deltaPct = (deltaY / Math.max(1, rect.height)) * 100;
    setSplitTopPct(Math.max(20, Math.min(80, d.startPct + deltaPct)));
  }, []);

  const endDrag = useCallback(() => {
    if (splitDragRef.current) splitDragRef.current.dragging = false;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
    };
  }, [onMouseMove, endDrag]);

  /* ------------------------------------------------------------------ */
  /*  Cards view — ACL role lookup + country flag resolution             */
  /* ------------------------------------------------------------------ */

  /** Map: project_guid (upper) → role string for the current user. */
  const userRoleByProject = useMemo(() => {
    const m = new Map<string, string>();
    const me = currentUserGuid.toUpperCase();
    if (!me) return m;
    for (const acl of apiProjectAcl || []) {
      if (String(acl.user_guid || '').toUpperCase() === me && acl.is_active !== false) {
        m.set(String(acl.project_guid || '').toUpperCase(), String(acl.role || 'viewer'));
      }
    }
    return m;
  }, [apiProjectAcl, currentUserGuid]);

  /** Convert an ISO 3166-1 alpha-2 country code to its flag emoji. */
  const countryFlagEmoji = useCallback((code: string | null | undefined): string => {
    if (!code || code.length < 2) return '';
    const upper = code.toUpperCase().trim();
    const cc = upper.indexOf('-') === 2 ? upper.slice(0, 2) : (upper.length === 2 ? upper : '');
    if (!cc || !/^[A-Z]{2}$/.test(cc)) return '';
    return String.fromCodePoint(...Array.from(cc).map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
  }, []);

  /** Entity map + parent map for country flag resolution. */
  const { entityMap, parentMap } = useMemo(() => {
    const em = new Map<string, Record<string, any>>();
    for (const e of apiCommunityEntities || []) {
      em.set(String(e.entity_guid || '').toUpperCase(), e);
    }
    const pm = new Map<string, string>();
    for (const r of apiCommunityRelationships || []) {
      pm.set(
        String(r.child_entity_guid || '').toUpperCase(),
        String(r.parent_entity_guid || '').toUpperCase()
      );
    }
    return { entityMap: em, parentMap: pm };
  }, [apiCommunityEntities, apiCommunityRelationships]);

  /**
   * Resolve country flag + local geography name for a project.
   * The **flag** comes from the ancestor geo_country, but the **name** is
   * the lowest-level entity (the project's primary location, e.g. "Boston").
   */
  const resolveCountryInfo = useCallback(
    (primaryLocationGuid: string | null | undefined): { flag: string; name: string } => {
      const empty = { flag: '', name: '' };
      if (!primaryLocationGuid) return empty;
      const startGuid = primaryLocationGuid.toUpperCase();

      // Capture the starting entity's name — that's the label we display
      const startEntity = entityMap.get(startGuid);
      const localName = startEntity ? String(startEntity.name || '') : '';

      // Walk up to find the country flag
      let guid: string | undefined = startGuid;
      for (let i = 0; i < 6 && guid; i++) {
        const entity = entityMap.get(guid);
        if (!entity) break;
        const etype = String(entity.entity_type || '').toLowerCase();
        const code = entity.code;
        if (etype === 'geo_country' && code) {
          const flag = countryFlagEmoji(code);
          return { flag, name: localName || String(entity.name || code) };
        }
        if (code) {
          const flag = countryFlagEmoji(code);
          if (flag) return { flag, name: localName || String(entity.name || code) };
        }
        guid = parentMap.get(guid);
      }
      return empty;
    },
    [entityMap, parentMap, countryFlagEmoji]
  );

  /** Open project detail panel (right side) when a card is clicked. */
  const handleCardClick = useCallback(
    (projectGuid: string) => {
      const row = (data || []).find(
        (d: any) => String(d?.project_guid ?? d?.project_GUID ?? '').toUpperCase() === projectGuid.toUpperCase()
      );
      if (row) {
        handleSelectProject(row, -1);
      }
    },
    [data, handleSelectProject]
  );

  /* ---- Build community path for a project (leaf + ancestors, single line) ---- */
  const buildCommunityPath = useCallback(
    (locationGuid: string): CommunityPathSegment[] => {
      if (!locationGuid) return [];
      const guid = locationGuid.toUpperCase();
      const leafEntity = entityMap.get(guid);
      if (!leafEntity) return [];

      const ancestors = resolveCommunityAncestors(locationGuid);
      const leafName = String(leafEntity.name || '');
      const leafConfig = communityConfigStore.get(guid);
      const leafActive = leafConfig?.is_active === true || leafEntity?.is_active === true;

      const segments: CommunityPathSegment[] = [];
      if (leafName) {
        segments.push({ guid, name: leafName, isActive: leafActive });
      }
      for (let i = ancestors.length - 1; i >= 0; i--) {
        const a = ancestors[i];
        const aConfig = communityConfigStore.get(a.guid.toUpperCase());
        const aEntity = entityMap.get(a.guid.toUpperCase());
        const aActive = aConfig?.is_active === true || aEntity?.is_active === true;
        segments.push({ guid: a.guid, name: a.name, isActive: aActive });
      }
      return segments;
    },
    [entityMap]
  );

  const handleCommunityClick = useCallback(
    (communityGuid: string) => {
      navigate(`/communities/${communityGuid}`);
    },
    [navigate]
  );

  /* ------------------------------------------------------------------ */
  /*  KPI Viewer (left panel content)                                    */
  /* ------------------------------------------------------------------ */

  const viewbarOverMap = myProjectsView === 'map' || myProjectsView === 'split';

  const kpiViewer = (
    <>
      <div
        className={`l4e-myprojects-viewbar${viewbarOverMap ? ' l4e-myprojects-viewbar--floating' : ''}`}
      >
        <div className="l4e-myprojects-viewbar-left">
          <div className="l4e-viewbtns" role="group" aria-label="View">
            <button className={`l4e-viewbtn ${myProjectsView === 'table' ? 'active' : ''}`} onClick={() => setMyProjectsView('table')} title="Table" type="button">
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M3 5h18v14H3V5zm2 2v2h6V7H5zm8 0v2h6V7h-6zM5 11v2h6v-2H5zm8 0v2h6v-2h-6zM5 15v2h6v-2H5zm8 0v2h6v-2h-6z" /></svg>
            </button>
            <button className={`l4e-viewbtn ${myProjectsView === 'map' ? 'active' : ''}`} onClick={() => setMyProjectsView('map')} title="Map" type="button">
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M12 2c-3.3 0-6 2.7-6 6 0 4.5 6 14 6 14s6-9.5 6-14c0-3.3-2.7-6-6-6zm0 8.2c-1.2 0-2.2-1-2.2-2.2S10.8 5.8 12 5.8s2.2 1 2.2 2.2S13.2 10.2 12 10.2z" /></svg>
            </button>
            <button className={`l4e-viewbtn ${myProjectsView === 'split' ? 'active' : ''}`} onClick={() => setMyProjectsView('split')} title="Split" type="button">
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M4 5h16v14H4V5zm2 2v10h6V7H6zm8 0v10h4V7h-4z" /></svg>
            </button>
            <button className={`l4e-viewbtn ${myProjectsView === 'cards' ? 'active' : ''}`} onClick={() => setMyProjectsView('cards')} title={t('myProjects.cardsView')} type="button">
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" /></svg>
            </button>
          </div>

          <div className="l4e-projectsearch">
            <svg width="16" height="16" viewBox="0 0 24 24"><path d="M10 2a8 8 0 105.3 14l4.2 4.2 1.4-1.4-4.2-4.2A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z" /></svg>
            <input className="l4e-projectsearch-input" value={searchText} onChange={(e) => onSearchTextChange(e.target.value)} placeholder={t('myProjects.filterProjects')} spellCheck={false} />
            {searchTerms.length > 0 && (
              <button className="l4e-projectsearch-clear" onClick={() => onSearchTextChange('')} title={t('myProjects.clear')} type="button">×</button>
            )}
          </div>
        </div>

        <div className="l4e-myprojects-viewbar-right">
          <button
            onClick={() => navigate('/projects/new')}
            type="button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 14px',
              borderRadius: 8,
              border: '1px solid var(--accent-bg)',
              background: 'var(--accent-bg)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + {t('myProjects.newProject')}
          </button>
          {/* project count removed — may revisit later */}
        </div>
      </div>

      <div className="col-content" style={{ minHeight: 0 }}>
        {myProjectsView === 'table' && (
          <KpiGridTable
            key={`kgt-${gridKeyBase}`}
            data={filteredTableData}
            onSelect={handleSelectProject}
            externalFocusKpiField={tabIndexToField(activeKpiTabIndex >= 0 ? activeKpiTabIndex : 0)}
            externalFocusRowId={selectedProjectGuid || ''}
            kpiMode={kpiMode}
            onKpiModeChange={onKpiModeChange}
            debugMode={debugMode}
            onDebugModeChange={onDebugModeChange}
            currentUserGuid={currentUserGuid}
            onUserChange={onUserChange}
            authorizedCollectionGuids={authorizedCollectionGuids}
            apiCollections={apiCollections}
            apiCollectionProjects={apiCollectionProjects}
            apiSchemaFields={apiSchemaFields}
            apiMetadataValues={apiMetadataValues}
            apiCustomEntities={apiCustomEntities}
            searchText={searchText}
            onSearchTextChange={onSearchTextChange}
            hideSearch={true}
          />
        )}

        {myProjectsView === 'map' && (
          <div style={{ height: '100%', width: '100%' }}>
            <ProjectMap
              points={filteredMapPoints as any}
              onProjectClick={handleMapClick}
              showColorBy={true}
              selectedProjectGuid={selectedProjectGuid || undefined}
              pulseProjectGuid={pulseProjectGuid || undefined}
            />
          </div>
        )}

        {myProjectsView === 'split' && (
          <div ref={splitContainerRef} className="l4e-split-vertical">
            <div style={{ height: `${splitTopPct}%`, minHeight: 0 }}>
              <ProjectMap
                points={filteredMapPoints as any}
                onProjectClick={handleMapClick}
                showColorBy={true}
                selectedProjectGuid={selectedProjectGuid || undefined}
                pulseProjectGuid={pulseProjectGuid || undefined}
              />
            </div>
            <div className="l4e-resize-handle" onMouseDown={beginDrag} />
            <div style={{ height: `${100 - splitTopPct}%`, minHeight: 0 }}>
              <KpiGridTable
                key={`kgt-${gridKeyBase}-split`}
                data={filteredTableData}
                onSelect={handleSelectProject}
                externalFocusKpiField={tabIndexToField(activeKpiTabIndex >= 0 ? activeKpiTabIndex : 0)}
                externalFocusRowId={selectedProjectGuid || ''}
                kpiMode={kpiMode}
                onKpiModeChange={onKpiModeChange}
                debugMode={debugMode}
                onDebugModeChange={onDebugModeChange}
                currentUserGuid={currentUserGuid}
                onUserChange={onUserChange}
                authorizedCollectionGuids={authorizedCollectionGuids}
                apiCollections={apiCollections}
                apiCollectionProjects={apiCollectionProjects}
                apiSchemaFields={apiSchemaFields}
                apiMetadataValues={apiMetadataValues}
                apiCustomEntities={apiCustomEntities}
                searchText={searchText}
                onSearchTextChange={onSearchTextChange}
                hideSearch={true}
              />
            </div>
          </div>
        )}

        {myProjectsView === 'cards' && (
          <div style={{ padding: '0 4px', overflow: 'auto', height: '100%', width: '100%', flex: '1 1 0', minWidth: 0 }}>
            {filteredTableData.length === 0 && (
              <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '3rem 1rem' }}>
                {searchText ? t('myProjects.noMatchingProjects') : t('myProjects.noProjects')}
              </div>
            )}
            <div className={cardStyles.grid}>
              {filteredTableData.length <= 2 && <CreateProjectCard />}
              {filteredTableData.map((row: any) => {
                const guid = String(row?.project_guid || row?.project_GUID || '').toUpperCase();
                const project = row?.project || row || {};
                const role = userRoleByProject.get(guid);
                const locationGuid = project.primary_location_entity_guid || '';
                const { flag, name } = resolveCountryInfo(locationGuid);
                return (
                  <ProjectCard
                    key={guid}
                    project={{
                      project_guid: guid,
                      project_name: project.project_name || '(unnamed)',
                      card_image_url: project.card_image_url || null,
                      hero_image_url: project.hero_image_url || project.tile_image_url || null,
                      project_category: project.project_category || '',
                      project_status: project.project_status || '',
                      project_reach: project.project_reach || '',
                      description: project.description || '',
                      Community: project.Community || project.community_l3 || '',
                      primary_location_entity_guid: locationGuid,
                      visibility: project.visibility || '',
                    }}
                    role={role}
                    countryFlag={flag}
                    countryName={name}
                    selected={guid === (selectedGuid || '').toUpperCase()}
                    onClick={handleCardClick}
                    communityPath={buildCommunityPath(locationGuid)}
                    onCommunityClick={handleCommunityClick}
                    kpiRow={row}
                    onKpiClick={(pguid, kpiTabIndex) => {
                      handleSelectProject(row, kpiTabIndex);
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );

  /* ------------------------------------------------------------------ */
  /*  Detail panel (right side)                                          */
  /* ------------------------------------------------------------------ */

  const detailPanel = selectedGroupPayload ? (
    <GroupProgressPage
      groupPayload={selectedGroupPayload}
      onBack={handleGroupBack}
      onSelectProject={handleGroupSelectProject}
      kpiCategory={
        activeKpiTabIndex === 2 ? "finance"
        : activeKpiTabIndex === 3 ? "mission"
        : activeKpiTabIndex === 4 ? "data"
        : activeKpiTabIndex === 0 ? "summary"
        : "schedule"
      }
      kpiCategoryLabel={
        activeKpiTabIndex === 2 ? "Budget"
        : activeKpiTabIndex === 3 ? "Mission"
        : activeKpiTabIndex === 4 ? "Data Reliability"
        : activeKpiTabIndex === 0 ? "Summary"
        : "Schedule"
      }
    />
  ) : selectedProjectRow ? (
    <ProjectDetailPanel
      projectRow={selectedProjectRow}
      currentUserGuid={currentUserGuid}
      debugMode={debugMode}
      onProjectConfigChanged={onProjectConfigChanged}
      onClose={handleCloseDetail}
      kpiTabIndex={activeKpiTabIndex}
      tabRequestId={tabRequestId}
      onKpiTabChange={handleDetailKpiTabChange}
      viewMode="leader"
      initialWorkSubTab={restoreWorkSubTab as any}
    />
  ) : (
    <PortfolioSummaryPanel />
  );

  /* ------------------------------------------------------------------ */
  /*  Responsive: full-screen detail takeover on narrow screens          */
  /* ------------------------------------------------------------------ */

  if (useFullScreenDetail && selectedProjectRow) {
    return (
      <div ref={containerRef} className="content" style={{ flexDirection: 'column' }}>
        {detailPanel}
      </div>
    );
  }

  /* Narrow viewport, no project selected → give the KPI view full width
     instead of wasting half the screen on an empty detail panel. */
  if (useFullScreenDetail) {
    return (
      <div ref={containerRef} className="content" style={{ flexDirection: 'column' }}>
        <section className="col col-left" style={{ flex: 1, maxWidth: '100%', height: '100%' }}>
          {kpiViewer}
        </section>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Render: resizable horizontal split (KPI left, detail right)        */
  /* ------------------------------------------------------------------ */

  return (
    <div ref={containerRef} className="content" style={{ flexDirection: 'column' }}>
      <ResizableSplitPane
        left={
          <section className="col col-left" style={{ flex: 1, maxWidth: '100%', height: '100%' }}>
            {kpiViewer}
          </section>
        }
        right={detailPanel}
        initialRatio={0.55}
        minWidth={300}
      />
    </div>
  );
}
